/**
 * @file Redirects to album folder join type preferences screen if necessary.
 * A temporary component while we're running Unbounce A/B tests.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import qs from 'query-string';

import { setJoinTypePreselectionVisibility as setJoinTypePreselectionVisibilityAction } from '../../../actions/discover-album-folder';
import { ALBUM_FOLDER_UB_REDIRECTS } from '../../../constants/unbounce';

/**
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithRedirectToJoinTypePreselectionHOC = WrappedComponent =>
  class WithRedirectToJoinTypePreselection extends Component {
    static propTypes = {
      albumFolderId: PropTypes.string.isRequired,
      location: PropTypes.shape({
        search: PropTypes.string,
      }).isRequired,
      setJoinTypePreselectionVisibility: PropTypes.func.isRequired,
      hasPreselectedJoinType: PropTypes.bool.isRequired,
    };

    componentDidMount() {
      const {
        albumFolderId,
        hasPreselectedJoinType,
        location,
        setJoinTypePreselectionVisibility,
      } = this.props;
      const { joinType } = qs.parse(location.search);

      // If we have a `joinType` query param it means we're coming from
      // the join type selection screen (Unbounce for now).
      // This will set the `hasPreselectedJoinType` flag to `true` and
      // will avoid redirecting the user on subsequent visits of this page
      // during the same session.
      if (joinType) {
        setJoinTypePreselectionVisibility(true);
        return;
      }

      // If no `joinType` query param exists, for very specific (hardcoded for now)
      // album folders, and if the `hasPreselectedJoinType` flag is false we redirect
      // the user to the Unbounce pages for join type selection.
      const redirectIds = Object.keys(ALBUM_FOLDER_UB_REDIRECTS);
      if (
        redirectIds.includes(albumFolderId) &&
        !hasPreselectedJoinType &&
        !joinType
      ) {
        window.location = `https://events.waldo.photos/${ALBUM_FOLDER_UB_REDIRECTS[albumFolderId]}`;
      }
    }

    render() {
      return <WrappedComponent {...this.props} />;
    }
  };

/**
 * Higher order component for determining whether to redirect the user
 * to join type selection.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const RedirectToJoinTypePreselection = WrappedComponent => {
  const WithRedirectToJoinTypePreselection =
    WithRedirectToJoinTypePreselectionHOC(WrappedComponent);

  const mapStateToProps = ({ discoverAlbumFolder }) => ({
    albumFolderId: discoverAlbumFolder.albumFolderId,
    hasPreselectedJoinType: discoverAlbumFolder.hasPreselectedJoinType,
  });

  const mapDispatchToProps = {
    setJoinTypePreselectionVisibility: setJoinTypePreselectionVisibilityAction,
  };

  return compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
  )(WithRedirectToJoinTypePreselection);
};

export { WithRedirectToJoinTypePreselectionHOC };
export default RedirectToJoinTypePreselection;
